import React from 'react';
import { ElementGroupNames } from '../../../../../common/src/enums/ElementGroupNames';
import { FeatureFlags } from '../../../../../common/src/FeatureFlags';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { fetchDelete, fetchGet } from '../../../store/fetch';
import { openApiFetchPost } from '../../../store/openApiFetch';
import { getFailureCause, getFailureOrigin } from '../../../utils/data/fmecaDataDefinition';
import { hasFeatureFlag } from '../../../utils/hasFeatureFlag';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelButton,
    PanelBody,
    PanelButtons,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    Tray,
} from '../../elements';
import { ComponentSetStatePromised } from '../../utils/ComponentSetStatePromised';
import { showSavedToast } from '../../utils/toastMessages';
import { TotalRiskScoreDot } from '../../utils/TotalRiskScoreDot';
import { SurveyEditCraAnalysis } from '../CRA/SurveyEditCraAnalysis';
import { IFailureMode } from '../Interfaces/IFailureMode';
import { AnalysisFailureModeAddEdit } from './FailureModeAnalysisAddEdit';
import { getUnitFailureModesToCopy } from './getUnitFailureModesToCopy';
import { SurveyEditCopyFailureMode } from './SurveyEditCopyFailureMode';
import { SurveyEditFmecaInspectionPlanAnalysis } from './SurveyEditFmecaInspectionPlanAnalysis';

interface ISurveyEditFmecaAnalysisProps extends ITranslateProps {
    surveyId: string;
    objectId: string;
    elementId?: string | undefined;
    elementGroupName?: string | undefined | null;
    unitId?: string | undefined;
    manifestationId?: string | undefined;
    failureModeId: string;
    selectedName?: string | undefined;
    selectedFailureModeUpdate: (selectedFailureModeId: string | undefined) => void;
    isCraAvailable: boolean;
    isFmecaAvailable: boolean;
    onItemChanged(): void;
}

interface ISurveyEditFmecaAnalysisState {
    failureModes: IFailureMode[];
    unitFailureModes: IFailureMode[];
    showAddEdit: boolean;
    showCopy: boolean;
    selectedFailureModeId?: string | undefined;
    selectedFailureModeIsDeletable?: boolean | undefined;
    isSaved: boolean;
    shouldShowCopyFailureModeButton: boolean;
}

class SurveyEditFmecaAnalysisComponent extends ComponentSetStatePromised<ISurveyEditFmecaAnalysisProps, ISurveyEditFmecaAnalysisState> {
    constructor(props: ISurveyEditFmecaAnalysisProps) {
        super(props);

        this.state = {
            failureModes: [],
            unitFailureModes: [],
            showAddEdit: false,
            showCopy: false,
            isSaved: false,
            shouldShowCopyFailureModeButton: false,
        };
    }

    public override async componentDidMount() {
        await this.loadFailureModes();
        await this.selectFailureMode(this.props.failureModeId);
        await this.loadUnitFailureModes();
    }

    public override async componentDidUpdate(prevProps: ISurveyEditFmecaAnalysisProps) {
        const shouldReload =
            prevProps.elementId !== this.props.elementId ||
            prevProps.unitId !== this.props.unitId ||
            prevProps.manifestationId !== this.props.manifestationId;

        if (shouldReload) {
            await this.setStatePromised({ selectedFailureModeId: undefined, showAddEdit: false, isSaved: false, showCopy: false });
            await this.loadFailureModes();
            await this.loadUnitFailureModes();
        }
    }

    public override render() {
        return (
            <Tray>
                {this.showCra() && !this.props.manifestationId && (
                    <EditPanel bottomMargin={true} noPadding={true}>
                        <CollapsiblePanel
                            heading={{
                                dark: true,
                                text: `${this.props.t('cra.constructionRiskAssessment')}: ${this.props.selectedName}`,
                                initialIsClosed: this.props.isFmecaAvailable,
                            }}
                        >
                            <SurveyEditCraAnalysis
                                {...this.props}
                                onSaved={() => {
                                    showSavedToast(this.props.t);
                                    this.props.onItemChanged();
                                }}
                            />
                        </CollapsiblePanel>
                    </EditPanel>
                )}
                <EditPanel bottomMargin={true} noPadding={true}>
                    {this.props.isFmecaAvailable && (
                        <>
                            <CollapsiblePanel heading={{ dark: true, text: this.getHeader(), initialIsClosed: false }}>
                                <PanelBody isForm={true}>
                                    <PanelTable hover={true}>
                                        <TableHead>
                                            <TableHeadRow>
                                                <TableHeader>
                                                    {this.props.t('common.unit')}/{this.props.t('common.manifestation')}
                                                </TableHeader>
                                                <TableHeader>{this.props.t('failureMode.failureMode')}</TableHeader>
                                                <TableHeader>{this.props.t('failureMode.failureCause')}</TableHeader>
                                                <TableHeader>{this.props.t('failureMode.failureOrigin')}</TableHeader>
                                                <TableHeader>{this.props.t('failureMode.analysisRiskScoreTableHeader')}</TableHeader>
                                            </TableHeadRow>
                                        </TableHead>
                                        <TableBody>
                                            {this.state.failureModes.map((failureMode) => (
                                                <TableRow
                                                    key={failureMode.id}
                                                    isRowSelected={failureMode.id === this.state.selectedFailureModeId}
                                                    onClick={async () => this.selectFailureMode(failureMode.id)}
                                                >
                                                    <TableData>
                                                        {failureMode.unit.name}{' '}
                                                        {failureMode.manifestation.name ? ` - ${failureMode.manifestation.name}` : ''}
                                                    </TableData>
                                                    <TableData>
                                                        {failureMode.defaultFailureMode.name
                                                            ? failureMode.defaultFailureMode.name
                                                            : failureMode.customName}
                                                    </TableData>
                                                    <TableData>{getFailureCause(failureMode.metaData)}</TableData>
                                                    <TableData>{getFailureOrigin(failureMode.metaData)}</TableData>
                                                    <TableData>
                                                        <TotalRiskScoreDot value={failureMode.analysisRamsWeightedPriority} />{' '}
                                                    </TableData>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </PanelTable>

                                    <PanelButtons>
                                        {this.shouldShowAddFailureModeButton() && (
                                            <EditPanelButton
                                                onClick={() => this.showAddFailureMode()}
                                                text={this.props.t('common.addItem', this.props.t('failureMode.failureMode'))}
                                                iconClass="fa-plus"
                                                small={true}
                                                primary={true}
                                            />
                                        )}
                                        {this.state.shouldShowCopyFailureModeButton && (
                                            <EditPanelButton
                                                text={this.props.t('failureMode.copyFromUnit')}
                                                onClick={async () => this.showCopyFailureMode()}
                                                small
                                            />
                                        )}
                                    </PanelButtons>

                                    {this.state.showAddEdit && (
                                        <AnalysisFailureModeAddEdit
                                            step="analysis"
                                            canRemove={this.state.selectedFailureModeIsDeletable}
                                            onCancel={() => this.setState({ showAddEdit: false })}
                                            onSaved={async () => this.failureModeSaved()}
                                            surveyId={this.props.surveyId}
                                            objectId={this.props.objectId}
                                            elementId={this.props.elementId}
                                            unitId={this.props.unitId}
                                            manifestationId={this.props.manifestationId}
                                            failureModeId={this.state.selectedFailureModeId}
                                            onRemove={async (id) => this.remove(id)}
                                        />
                                    )}
                                    {this.state.showCopy && this.props.manifestationId && (
                                        <SurveyEditCopyFailureMode
                                            unitFailureModes={this.state.unitFailureModes}
                                            surveyId={this.props.surveyId}
                                            manifestationId={this.props.manifestationId}
                                            onCancel={() => this.setState({ showCopy: false })}
                                            onSaved={async () => this.failureModeSaved('copy')}
                                        />
                                    )}
                                </PanelBody>
                            </CollapsiblePanel>
                        </>
                    )}
                </EditPanel>
                {hasFeatureFlag(FeatureFlags.Cra) && (this.props.unitId || this.props.manifestationId) && (
                    <SurveyEditFmecaInspectionPlanAnalysis {...this.props} />
                )}
            </Tray>
        );
    }

    private showCra() {
        return (
            hasFeatureFlag(FeatureFlags.Cra) &&
            this.props.isCraAvailable &&
            this.props.unitId &&
            !this.props.manifestationId &&
            this.props.elementGroupName !== undefined &&
            [ElementGroupNames.Foundation, ElementGroupNames.Substructure, ElementGroupNames.Superstructure].includes(
                this.props.elementGroupName as ElementGroupNames,
            )
        );
    }

    private async selectFailureMode(id: string) {
        if (this.state.selectedFailureModeId === id) {
            await this.setStatePromised({
                selectedFailureModeId: undefined,
                selectedFailureModeIsDeletable: false,
                showAddEdit: false,
                showCopy: false,
            });
            this.props.selectedFailureModeUpdate(undefined);
        } else {
            const selectedFailureMode = this.state.failureModes.filter((item: any) => item.id === id)[0]!;
            const canDeleteFailureMode =
                selectedFailureMode.findings &&
                selectedFailureMode.findings.length === 0 &&
                selectedFailureMode.assets &&
                selectedFailureMode.assets.length === 0 &&
                selectedFailureMode.maintenanceMeasures &&
                selectedFailureMode.maintenanceMeasures.length === 0;
            await this.setStatePromised({
                selectedFailureModeId: id,
                selectedFailureModeIsDeletable: canDeleteFailureMode,
                showAddEdit: true,
                showCopy: false,
            });
            this.props.selectedFailureModeUpdate(id);
        }
    }

    private async failureModeSaved(action?: 'copy') {
        await this.setStatePromised({ showAddEdit: false, showCopy: false });
        if (action !== 'copy') {
            void showSavedToast(this.props.t);
        }
        await this.loadFailureModes();
        await this.loadUnitFailureModes();
        this.props.onItemChanged();
    }

    private showAddFailureMode() {
        this.setState({ showAddEdit: true, selectedFailureModeId: undefined, showCopy: false });
    }
    private async showCopyFailureMode() {
        if (this.state.unitFailureModes.length === 1) {
            const unitFailureMode = this.state.unitFailureModes[0]!;
            await openApiFetchPost(
                '/api/surveys/{surveyId}/failureModes/copy',
                { surveyId: this.props.surveyId },
                { manifestationId: this.props.manifestationId!, failureModeId: unitFailureMode.id },
            );
            await this.failureModeSaved();
        } else {
            this.setState({ showAddEdit: false, selectedFailureModeId: undefined, showCopy: true });
        }
    }

    private shouldShowAddFailureModeButton() {
        if (this.props.unitId || this.props.manifestationId) {
            return true;
        }
        return false;
    }

    private getHeader() {
        if (!this.props.elementId && !this.props.unitId && !this.props.manifestationId) {
            return this.props.t('failureMode.allFailureModes');
        }
        return `${this.props.t('failureMode.failureModes')}: ${this.props.selectedName}`;
    }

    private async loadFailureModes() {
        const url = `/api/surveys/${this.props.surveyId}/failureModes`;

        const result = await fetchGet(url, {
            elementId: this.props.elementId,
            unitId: this.props.unitId,
            manifestationId: this.props.manifestationId,
            includeAssets: 'true',
            includeFindings: 'true',
            includeMaintenance: 'true',
        });
        await this.setStatePromised({ failureModes: result.json });
    }

    private async loadUnitFailureModes() {
        if (this.props.manifestationId) {
            const failureModesToCopy = await getUnitFailureModesToCopy(
                this.props.surveyId,
                this.props.elementId!,
                this.props.unitId!,
                this.state.failureModes,
            );
            await this.setStatePromised({ shouldShowCopyFailureModeButton: failureModesToCopy.length > 0, unitFailureModes: failureModesToCopy });
        } else {
            await this.setStatePromised({ shouldShowCopyFailureModeButton: false });
        }
    }

    private async remove(id: string) {
        await this.setStatePromised({ showAddEdit: false, selectedFailureModeId: undefined, showCopy: false });
        await fetchDelete(`/api/surveys/${this.props.surveyId}/failureModes/${id}`);

        await this.failureModeSaved();
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const SurveyEditFMECAAnalysis = translate()(SurveyEditFmecaAnalysisComponent);
